/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @compiled/react */
import React, { ReactNode } from 'react';
import AKAvatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import { Box, Flex, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge';

interface Props {
  displayName?: string;
  /** When provided, the Account is styled as a button. */
  onClick?: (_: React.MouseEvent<HTMLElement>) => void;
  email: string;
  primary?: boolean;
  avatarUrl?: string;
  icon?: ReactNode;
  isolatedCloudLabel?: string;
  id?: string;
}

interface AccountContainerSharedProps {
  id?: string;
  children: ReactNode;
  testId?: string;
}

interface AccountContainerProps extends AccountContainerSharedProps {
  onClick?: (_: React.MouseEvent<HTMLElement>) => void;
}

interface AccountButtonContainerProps extends AccountContainerSharedProps {
  onClick: (_: React.MouseEvent<HTMLElement>) => void;
}

const styles = cssMap({
  accountContainer: {
    width: '100%',
    paddingBlock: token('space.100'),
  },
  accountButtonContainer: {
    width: '100%',
    paddingBlock: token('space.200'),
    backgroundColor: token('color.background.neutral.subtle'),
    borderBottomStyle: 'solid',
    borderColor: token('color.border'),
    borderWidth: token('border.width'),
    '&:hover': {
      borderColor: 'transparent',
      borderRadius: token('radius.small'),
      backgroundColor: token('color.background.neutral.subtle.hovered'),
    },
    '&:focus': {
      borderColor: 'transparent',
      borderRadius: token('radius.small'),
      backgroundColor: token('color.background.neutral.subtle.hovered'),
    },
    '&:active': {
      borderColor: 'transparent',
      borderRadius: token('radius.small'),
      backgroundColor: token('color.background.neutral.subtle.pressed'),
    },
  },
  accountWrapper: {
    alignItems: 'center',
    paddingInline: token('space.100'),
    width: '100%',
  },
  textContainer: {
    flexGrow: 1,
    textAlign: 'start',
    marginInline: token('space.200'),
  },
  topTextContainer: {
    alignItems: 'center',
  },
});

const AccountBoxContainer = ({ id, children, testId }: AccountContainerSharedProps) => (
  <Box id={id} testId={testId} xcss={styles.accountContainer}>
    {children}
  </Box>
);

const AccountButtonContainer = ({ id, onClick, children, testId }: AccountButtonContainerProps) => (
  <Pressable
    id={id}
    testId={testId}
    xcss={styles.accountButtonContainer}
    onClick={onClick}
    type="button">
    {children}
  </Pressable>
);

const AccountContainer = ({ id, onClick, children, testId }: AccountContainerProps) => (
  <>
    {onClick ? (
      <AccountButtonContainer id={id} onClick={onClick} testId={testId}>
        {children}
      </AccountButtonContainer>
    ) : (
      <AccountBoxContainer id={id} testId={testId}>
        {children}
      </AccountBoxContainer>
    )}
  </>
);

/**
 * Representation of an account in a row format, used for rendering lists of accounts, e.g. for switching an account
 * or selecting one to log in
 */
const Account = ({
  avatarUrl,
  displayName,
  email,
  icon,
  isolatedCloudLabel,
  id,
  onClick,
  primary,
}: Props) => {
  return (
    <AccountContainer id={id} onClick={onClick} testId="account-entry">
      <Inline xcss={styles.accountWrapper}>
        <AKAvatar appearance="circle" src={avatarUrl} size={primary ? 'xlarge' : 'medium'} />

        <Stack xcss={styles.textContainer}>
          <Inline space="space.100" xcss={styles.topTextContainer}>
            <Text
              weight="bold"
              size={primary ? 'large' : 'medium'}
              testId="account-display-text-idf-testid">
              {displayName ? displayName : email}
            </Text>

            {isolatedCloudLabel && (
              <Flex>
                <Lozenge maxWidth="130px">{isolatedCloudLabel}</Lozenge>
              </Flex>
            )}
          </Inline>
          {displayName && <Text testId="account-secondary-text-idf-testid">{email}</Text>}
        </Stack>
        <Box>{icon}</Box>
      </Inline>
    </AccountContainer>
  );
};

export default Account;
